import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PlusCircle, Trash2, Mail, Send, ChevronRight, Package, Truck, ArrowLeft } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

export default function ProductRequestModal({ open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1); // 1: Lieferant, 2: Produkte, 3: E-Mail
  
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [items, setItems] = useState([{ artikelnummer: '', titel: '', variants: [{ value: '' }] }]);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('Anfrage für neue Produkte');

  // Load suppliers
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list(),
    enabled: open,
  });

  const safeSuppliers = Array.isArray(suppliers) ? suppliers : [];
  const selectedSupplier = safeSuppliers.find(s => s && s.id === selectedSupplierId) || {};

  // Mutation to create products
  const createProductsMut = useMutation({
    mutationFn: async (productsToCreate) => {
      // Promise.all to create all products in parallel
      return Promise.all(productsToCreate.map(p => base44.entities.Product.create(p)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      toast.success('Produkte wurden als inaktiv angelegt!');
      handleClose();
    },
    onError: (e) => toast.error('Fehler beim Anlegen: ' + e.message),
  });

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(1);
      setSelectedSupplierId('');
      setItems([{ artikelnummer: '', titel: '', variants: [{ value: '' }] }]);
      setEmailContent('');
    }, 300);
  };

  const addItem = () => setItems(prev => [...prev, { artikelnummer: '', titel: '', variants: [{ value: '' }] }]);
  
  const updateItem = (index, field, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index][field] = value;
      return newItems;
    });
  };

  const addVariant = (itemIndex) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[itemIndex].variants.push({ value: '' });
      return newItems;
    });
  };

  const updateVariant = (itemIndex, variantIndex, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[itemIndex].variants[variantIndex].value = value;
      return newItems;
    });
  };

  const removeVariant = (itemIndex, variantIndex) => {
    setItems(prev => {
      const newItems = [...prev];
      if (newItems[itemIndex].variants.length > 1) {
        newItems[itemIndex].variants = newItems[itemIndex].variants.filter((_, i) => i !== variantIndex);
      }
      return newItems;
    });
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const generateEmailContent = () => {
    let content = `Guten Tag,\n\nwir bitten um ein Angebot bzw. weitere Informationen zu folgenden Produkten:\n\n`;
    items.forEach((item, idx) => {
      content += `${idx + 1}. Titel: ${item.titel}\n`;
      if (item.artikelnummer) content += `   Artikelnummer: ${item.artikelnummer}\n`;
      const validVariants = item.variants.filter(v => v.value.trim() !== '');
      if (validVariants.length > 0) {
        content += `   Variante(n): ${validVariants.map(v => v.value).join(', ')}\n`;
      }
      content += `\n`;
    });
    content += `Vielen Dank im Voraus.\n\nMit freundlichen Grüßen,\nIhr InMotion Team`;
    setEmailContent(content);
  };

  const handleNextStep2 = () => {
    if (!selectedSupplierId || !selectedSupplier?.id) {
      toast.error('Bitte wählen Sie einen gültigen Lieferanten aus.');
      return;
    }
    setStep(2);
  };

  const handleNextStep3 = () => {
    // Validate items
    const isValid = items.every(item => item.titel.trim() !== '');
    if (!isValid) {
      toast.error('Bitte geben Sie für alle Zeilen mindestens einen Titel ein.');
      return;
    }
    generateEmailContent();
    setStep(3);
  };

  const handleSend = () => {
    // 1. Open mailto link if email exists
    if (selectedSupplier?.email) {
      const mailtoLink = `mailto:${selectedSupplier.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailContent)}`;
      window.location.href = mailtoLink;
    } else {
      toast.info('Lieferant hat keine E-Mail. Produkte werden dennoch gespeichert.');
    }

    // 2. Save products to database as inactive
    const productsToCreate = items.map(item => ({
      name: item.titel,
      sku: '', // Optional
      is_active: false,
      stock: 0,
      min_stock: 0,
      unit: 'Stück',
      suppliers: [
        {
          name: selectedSupplier.name,
          artikelnummer: item.artikelnummer,
          price: 0,
          delivery_time: selectedSupplier.delivery_time || ''
        }
      ],
      variants: item.variants.filter(v => v.value.trim() !== '').length > 0 
        ? item.variants.filter(v => v.value.trim() !== '').map(v => ({
            value: v.value,
            stock: 0,
            min_stock: 0,
            price: 0
          }))
        : [
            {
              value: 'Standard',
              stock: 0,
              min_stock: 0,
              price: 0
            }
          ]
    }));

    createProductsMut.mutate(productsToCreate);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleClose(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col p-0 overflow-hidden">
        
        {/* Header with Step Indicator */}
        <div className="bg-slate-900 text-white p-5 flex flex-col gap-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5 text-emerald-400" />
            Neue Produkte anfragen & anlegen
          </DialogTitle>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <span className={step >= 1 ? 'text-white' : 'text-slate-500'}>1. Lieferant</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className={step >= 2 ? 'text-white' : 'text-slate-500'}>2. Produkte</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className={step >= 3 ? 'text-white' : 'text-slate-500'}>3. E-Mail</span>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
          
          {step === 1 && (
            <div className="space-y-4 max-w-md mx-auto py-8">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Welcher Lieferant?
              </Label>
              <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
                <SelectTrigger className="h-12 bg-white shadow-sm border-slate-200">
                  <SelectValue placeholder="Lieferant auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-900">{s.name}</span>
                        {s.email && <span className="text-[10px] text-slate-500">{s.email}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <h3 className="font-bold text-slate-700 flex items-center gap-2">
                  <Package className="w-4 h-4 text-blue-500" /> Produkte erfassen
                </h3>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Lieferant: {selectedSupplier?.name}
                </Badge>
              </div>

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="flex gap-3 items-start bg-white p-3 rounded-xl border border-slate-200 shadow-sm relative group">
                    {items.length > 1 && (
                      <button 
                        onClick={() => removeItem(index)}
                        className="absolute -top-2 -right-2 bg-white border border-red-100 text-red-500 p-1 rounded-full shadow-sm hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <div className="flex-1 grid grid-cols-12 gap-3">
                      <div className="col-span-3 space-y-1.5">
                        <Label className="text-[10px] uppercase font-bold text-slate-400">LF-Artikelnr.</Label>
                        <Input 
                          placeholder="z.B. LF-123" 
                          value={item.artikelnummer} 
                          onChange={(e) => updateItem(index, 'artikelnummer', e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                      <div className="col-span-6 space-y-1.5">
                        <Label className="text-[10px] uppercase font-bold text-slate-400">Titel *</Label>
                        <Input 
                          placeholder="Produktname..." 
                          value={item.titel} 
                          onChange={(e) => updateItem(index, 'titel', e.target.value)}
                          className="h-9 text-xs font-medium"
                          autoFocus={index === items.length - 1}
                        />
                      </div>
                      <div className="col-span-3 space-y-1.5">
                        <Label className="text-[10px] uppercase font-bold text-slate-400">Variante(n)</Label>
                        <div className="space-y-2">
                          {item.variants.map((variant, vIndex) => (
                            <div key={vIndex} className="flex items-center gap-1">
                              <Input 
                                placeholder="z.B. 1000 Stück" 
                                value={variant.value} 
                                onChange={(e) => updateVariant(index, vIndex, e.target.value)}
                                className="h-9 text-xs"
                              />
                              {item.variants.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeVariant(index, vIndex)}
                                  className="text-slate-400 hover:text-red-500 p-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addVariant(index)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 mt-1"
                          >
                            <PlusCircle className="w-3 h-3" /> Variante hinzufügen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={addItem} variant="outline" className="w-full border-dashed border-2 bg-transparent hover:bg-slate-100 text-slate-500 hover:text-slate-700 h-12 gap-2 mt-2">
                <PlusCircle className="w-4 h-4" /> Weitere Zeile hinzufügen
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              {!selectedSupplier?.email && (
                <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg flex items-start gap-2 text-sm">
                  <Mail className="w-5 h-5 shrink-0" />
                  <p>
                    <strong>Achtung:</strong> Bei diesem Lieferanten ({selectedSupplier?.name}) ist keine E-Mail-Adresse hinterlegt.
                    Sie können die Produkte trotzdem speichern, aber es wird keine E-Mail geöffnet.
                  </p>
                </div>
              )}
              
              <div className="space-y-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-500">An (Lieferant)</Label>
                  <Input value={selectedSupplier?.email || 'Keine E-Mail hinterlegt'} disabled className="h-9 bg-slate-50 text-slate-500 text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-500">Betreff</Label>
                  <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="h-9 text-xs font-bold" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-500">Nachricht</Label>
                  <Textarea 
                    value={emailContent} 
                    onChange={(e) => setEmailContent(e.target.value)} 
                    rows={10} 
                    className="text-xs font-mono bg-slate-50 border-slate-200 leading-relaxed"
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-white p-4 border-t border-slate-200 flex justify-between items-center">
          {step > 1 ? (
            <Button variant="ghost" onClick={() => setStep(s => s - 1)} className="gap-2 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Zurück
            </Button>
          ) : <div></div>}

          {step === 1 && (
            <Button onClick={handleNextStep2} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 px-6">
              Weiter <ChevronRight className="w-4 h-4" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={handleNextStep3} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 px-6">
              Weiter <ChevronRight className="w-4 h-4" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={handleSend} disabled={createProductsMut.isPending} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-6 shadow-md font-bold">
              <Send className="w-4 h-4" /> 
              {selectedSupplier?.email ? 'Versenden & Speichern' : 'Nur Speichern'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
